/** @jsx jsx */
import type { ReactNode } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { Tokens } from '@jira/platform__project-theme-tokens/src/constants.tsx';
import { ThemeTokensReset } from '@jira/platform__project-theme-tokens-reset/src/ThemeTokensReset.tsx';
import {
	InnerWrapper,
	InnerWrapperWithBackground,
	OuterWrapper,
	OuterWrapperWithBackground,
} from './styled.tsx';

const styles = cssMap({
	root: {
		position: 'relative',
	},
	fullHeight: {
		minHeight: 0,
		height: '100%',
	},
	insetRoot: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		padding: Tokens.WRAPPER_PADDING,
	},
	insetContainer: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		borderRadius: Tokens.WRAPPER_BORDER_RADIUS,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundColor: Tokens.WRAPPER_BACKGROUND,
	},
});

/**
 * This component sets a fluid layout that works with the new page
 * layout created in @atlaskit/navigation-system. It does not use
 * explicit sizes set from CSS variables but grows to the space given.
 */
export const ThemedContentLayout = ({
	children,
	appearance,
}: {
	appearance: 'default' | 'inset';
	children: ReactNode;
}): JSX.Element => {
	const isInset = appearance === 'inset';

	// When in inset mode, wrap content with ThemeTokensReset to reset
	// --project-* tokens to Atlaskit defaults. This allows ThemedButton
	// to be used everywhere without isThemed/isInset checks.
	const content = isInset ? <ThemeTokensReset>{children}</ThemeTokensReset> : children;

	return (
		<div css={[styles.root, styles.fullHeight, isInset && styles.insetRoot]}>
			<div css={[styles.fullHeight, isInset && styles.insetContainer]}>{content}</div>
		</div>
	);
};

export const ProjectThemeWrapper = ({
	withWhiteBackground,
	children,
}: {
	withWhiteBackground: boolean;
	children: ReactNode;
}): JSX.Element => {
	if (withWhiteBackground) {
		return (
			<OuterWrapperWithBackground>
				<InnerWrapperWithBackground>{children}</InnerWrapperWithBackground>
			</OuterWrapperWithBackground>
		);
	}

	return (
		<OuterWrapper>
			<InnerWrapper>{children}</InnerWrapper>
		</OuterWrapper>
	);
};
